import React, { useRef, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import "react-phone-input-2/lib/style.css";
import "./MiniAgentForm.css";
import { BiImageAlt } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import Cropper from "react-easy-crop";
import { BiZoomIn, BiZoomOut } from "react-icons/bi";
import { FiRotateCw } from "react-icons/fi";
import { API_URL } from "../../constant";
import { isValidPostalCode } from "../../utils/postalCodeValidator";

export default function MiniAgentForm({ onSubmit }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const fileRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDataLoading, setUserDataLoading] = useState(true);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);

  // Photo crop state
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [tempImage, setTempImage] = useState(null);
  const [openCrop, setOpenCrop] = useState(false);

  // Initial customer state
  const [customer, setCustomer] = useState({
    firstName: "",
    surname: "",
    email: "",
    mobile: "",
    address: {
      line1: "",
      line2: "",
      line3: "",
      city: "",
      postcode: "",
      county: "",
      country: "",
    },
    profileImage: null,
    agree: false,
    agent_type: "mini",
  });

  // Error states
  const [profileError, setProfileError] = useState("");
  const [agreeError, setAgreeError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [surnameError, setSurnameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [line1Error, setLine1Error] = useState("");
  const [cityError, setCityError] = useState("");
  const [postCodeError, setPostCodeError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [fetchError, setFetchError] = useState("");

  const steps = [
    { label: "Profile Image", sub: "Upload your image" },
    { label: "Basic details", sub: "Enter basic details" },
    { label: "Address", sub: "Enter address details" },
  ];

  const regionOptions = {
    // Africa
    Afghanistan: ["Asia"],
    Albania: ["Europe"],
    Algeria: ["Africa"],
    Andorra: ["Europe"],
    Angola: ["Africa"],
    Argentina: ["South America"],
    Armenia: ["Asia"],
    Australia: ["Oceania"],
    Austria: ["Europe"],
    Azerbaijan: ["Asia"],
    Bahamas: ["North America"],
    Bahrain: ["Asia"],
    Bangladesh: ["Asia"],
    Barbados: ["North America"],
    Belarus: ["Europe"],
    Belgium: ["Europe"],
    Belize: ["North America"],
    Benin: ["Africa"],
    Bhutan: ["Asia"],
    Bolivia: ["South America"],
    "Bosnia and Herzegovina": ["Europe"],
    Botswana: ["Africa"],
    Brazil: ["South America"],
    Brunei: ["Asia"],
    Bulgaria: ["Europe"],
    "Burkina Faso": ["Africa"],
    Burundi: ["Africa"],
    Cambodia: ["Asia"],
    Cameroon: ["Africa"],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "Cape Verde": ["Africa"],
    "Central African Republic": ["Africa"],
    Chad: ["Africa"],
    Chile: ["South America"],
    China: ["Asia"],
    Colombia: ["South America"],
    Comoros: ["Africa"],
    Congo: ["Africa"],
    "Costa Rica": ["North America"],
    Croatia: ["Europe"],
    Cuba: ["North America"],
    Cyprus: ["Europe"],
    "Czech Republic": ["Europe"],
    Denmark: ["Europe"],
    Djibouti: ["Africa"],
    "Dominican Republic": ["North America"],
    Ecuador: ["South America"],
    Egypt: ["Africa"],
    "El Salvador": ["North America"],
    Estonia: ["Europe"],
    Ethiopia: ["Africa"],
    Finland: ["Europe"],
    France: ["Europe"],
    Germany: ["Europe"],
    Ghana: ["Africa"],
    Greece: ["Europe"],
    Guatemala: ["North America"],
    Haiti: ["North America"],
    Honduras: ["North America"],
    Hungary: ["Europe"],
    Iceland: ["Europe"],
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    Indonesia: ["Asia"],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    Iraq: ["Asia"],
    Ireland: ["Europe"],
    Israel: ["Asia"],
    Italy: ["Europe"],
    Jamaica: ["North America"],
    Japan: ["Asia"],
    Jordan: ["Asia"],
    Kazakhstan: ["Asia"],
    Kenya: ["Africa"],
    Kuwait: ["Asia"],
    Kyrgyzstan: ["Asia"],
    Laos: ["Asia"],
    Latvia: ["Europe"],
    Lebanon: ["Asia"],
    Lesotho: ["Africa"],
    Liberia: ["Africa"],
    Libya: ["Africa"],
    Lithuania: ["Europe"],
    Luxembourg: ["Europe"],
    Madagascar: ["Africa"],
    Malawi: ["Africa"],
    Malaysia: ["Asia"],
    Maldives: ["Asia"],
    Mali: ["Africa"],
    Malta: ["Europe"],
    Mexico: ["North America"],
    Moldova: ["Europe"],
    Mongolia: ["Asia"],
    Morocco: ["Africa"],
    Mozambique: ["Africa"],
    Myanmar: ["Asia"],
    Namibia: ["Africa"],
    Nepal: ["Asia"],
    Netherlands: ["Europe"],
    "New Zealand": ["Oceania"],
    Nicaragua: ["North America"],
    Niger: ["Africa"],
    Nigeria: ["Africa"],
    "North Korea": ["Asia"],
    Norway: ["Europe"],
    Oman: ["Asia"],
    Pakistan: ["Asia"],
    Panama: ["North America"],
    Paraguay: ["South America"],
    Peru: ["South America"],
    Philippines: ["Asia"],
    Poland: ["Europe"],
    Portugal: ["Europe"],
    Qatar: ["Asia"],
    Romania: ["Europe"],
    Russia: ["Europe"],
    Rwanda: ["Africa"],
    "Saudi Arabia": ["Asia"],
    Senegal: ["Africa"],
    Serbia: ["Europe"],
    Singapore: ["Asia"],
    Slovakia: ["Europe"],
    Slovenia: ["Europe"],
    Somalia: ["Africa"],
    "South Africa": ["Africa"],
    "South Korea": ["Asia"],
    Spain: ["Europe"],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Sudan: ["Africa"],
    Sweden: ["Europe"],
    Switzerland: ["Europe"],
    Syria: ["Asia"],
    Taiwan: ["Asia"],
    Tanzania: ["Africa"],
    Thailand: ["Asia"],
    Tunisia: ["Africa"],
    Turkey: ["Asia"],
    Uganda: ["Africa"],
    Ukraine: ["Europe"],
    "United Arab Emirates": ["Asia"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    "United States": ["North America"],
    Uruguay: ["South America"],
    Uzbekistan: ["Asia"],
    Venezuela: ["South America"],
    Vietnam: ["Asia"],
    Yemen: ["Asia"],
    Zambia: ["Africa"],
    Zimbabwe: ["Africa"],
  };

  const countryCodes = [
    "1",
    "7",
    "20",
    "27",
    "30",
    "31",
    "32",
    "33",
    "34",
    "36",
    "39",
    "40",
    "41",
    "43",
    "44",
    "45",
    "46",
    "47",
    "48",
    "49",
    "51",
    "52",
    "53",
    "54",
    "55",
    "56",
    "57",
    "58",
    "60",
    "61",
    "62",
    "63",
    "64",
    "65",
    "66",
    "81",
    "82",
    "84",
    "86",
    "90",
    "91",
    "92",
    "93",
    "94",
    "95",
    "98",
    "211",
    "212",
    "213",
    "216",
    "218",
    "220",
    "221",
    "222",
    "223",
    "224",
    "225",
    "226",
    "227",
    "228",
    "229",
    "230",
    "231",
    "232",
    "233",
    "234",
    "235",
    "236",
    "237",
    "238",
    "239",
    "240",
    "241",
    "242",
    "243",
    "244",
    "245",
    "246",
    "247",
    "248",
    "249",
    "250",
    "251",
    "252",
    "253",
    "254",
    "255",
    "256",
    "257",
    "258",
    "260",
    "261",
    "262",
    "263",
    "264",
    "265",
    "266",
    "267",
    "268",
    "269",
    "290",
    "291",
    "297",
    "298",
    "299",
    "350",
    "351",
    "352",
    "353",
    "354",
    "355",
    "356",
    "357",
    "358",
    "359",
    "370",
    "371",
    "372",
    "373",
    "374",
    "375",
    "376",
    "377",
    "378",
    "379",
    "380",
    "381",
    "382",
    "383",
    "385",
    "386",
    "387",
    "389",
    "420",
    "421",
    "423",
    "500",
    "501",
    "502",
    "503",
    "504",
    "505",
    "506",
    "507",
    "508",
    "509",
    "590",
    "591",
    "592",
    "593",
    "594",
    "595",
    "596",
    "597",
    "598",
    "599",
    "670",
    "672",
    "673",
    "674",
    "675",
    "676",
    "677",
    "678",
    "679",
    "680",
    "681",
    "682",
    "683",
    "685",
    "686",
    "687",
    "688",
    "689",
    "690",
    "691",
    "692",
    "850",
    "852",
    "853",
    "855",
    "856",
    "870",
    "878",
    "880",
    "886",
    "960",
    "961",
    "962",
    "963",
    "964",
    "965",
    "966",
    "967",
    "968",
    "970",
    "971",
    "972",
    "973",
    "974",
    "975",
    "976",
    "977",
    "992",
    "993",
    "994",
    "995",
    "996",
    "998",
  ];

  // Fetch user data and existing application on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setUserDataLoading(true);
      const token =
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("auth_token");
      const API_BASE_URL = API_URL;

      const userResponse = await fetch(`${API_BASE_URL}/api/customer/me`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();

        if (userData.user) {
          setCustomer((prev) => ({
            ...prev,
            firstName: userData.user.first_name || "",
            surname: userData.user.last_name || "",
            email: userData.user.email || "",
            mobile: userData.user.phone || "",
            address: {
              line1: userData.user.address_line1 || "",
              line2: userData.user.address_line2 || "",
              line3: userData.user.address_line3 || "",
              city: userData.user.city || "",
              postcode: userData.user.postcode || "", // Make sure postcode is included
              county: userData.user.region || "",
              country: userData.user.country || "",
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setUserDataLoading(false);
    }
  };

  // Image handling functions
  const onCropComplete = (_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height,
    );

    return canvas.toDataURL("image/jpeg");
  }

  const handleCropSave = async () => {
    const croppedImg = await getCroppedImg(
      tempImage,
      croppedAreaPixels,
      rotation,
    );
    setCustomer({ ...customer, profileImage: croppedImg });
    setOpenCrop(false);
  };

  const handleImageUpload = (file) => {
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        setTempImage(reader.result);
        setOpenCrop(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setCustomer((prev) => ({
      ...prev,
      profileImage: null,
    }));
  };

  // Validation functions
  const validateProfileImage = () => {
    if (!customer.profileImage) {
      setProfileError("Profile image is required");
      return false;
    }
    setProfileError("");
    return true;
  };

  const validateBasicDetails = () => {
    let isValid = true;

    if (!customer.firstName.trim()) {
      setFirstNameError("First name is required.");
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(customer.firstName)) {
      setFirstNameError("First name must contain only letters.");
      isValid = false;
    } else if (customer.firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      isValid = false;
    } else {
      setFirstNameError("");
    }

    if (!customer.surname.trim()) {
      setSurnameError("Surname is required.");
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(customer.surname)) {
      setSurnameError("Surname must contain only letters.");
      isValid = false;
    } else if (customer.surname.trim().length < 3) {
      setSurnameError("Surname must be at least 3 letters long.");
      isValid = false;
    } else {
      setSurnameError("");
    }

    if (!customer.email.trim()) {
      setEmailError("Email is required");
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) {
      setEmailError("Please enter a valid email address.");
      isValid = false;
    } else {
      setEmailError("");
    }

    // Enhanced Mobile validation
    if (!customer.mobile.trim()) {
      setMobileError("Mobile number is required.");
      isValid = false;
    } else {
      const digitsOnly = customer.mobile.replace(/\D/g, "");

      // Check if it contains at least a country code and some digits
      if (digitsOnly.length < 3) {
        setMobileError("Please enter a valid mobile number with country code.");
        isValid = false;
      } else {
        // Extract country code and national number
        let dialCode = "";
        let nationalNumber = "";

        for (let code of countryCodes) {
          if (digitsOnly.startsWith(code)) {
            dialCode = code;
            nationalNumber = digitsOnly.slice(dialCode.length);
            break;
          }
        }

        // If no country code found, assume first few digits are country code
        if (!dialCode && digitsOnly.length > 0) {
          setMobileError("Please select a valid country code.");
          isValid = false;
        } else if (nationalNumber.length === 0) {
          setMobileError("Please enter your mobile number.");
          isValid = false;
        } else if (nationalNumber.length < 5) {
          setMobileError("Mobile number is too short.");
          isValid = false;
        } else if (nationalNumber.length > 10) {
          setMobileError(
            `Mobile number must not exceed 10 digits (excluding country code +${dialCode})`,
          );
          isValid = false;
        } else {
          setMobileError("");
        }
      }
    }

    return isValid;
  };

  const validateAddressDetails = () => {
    let isValid = true;

    if (!customer.address.line1.trim()) {
      setLine1Error("Address Line 1 is required");
      isValid = false;
    } else {
      setLine1Error("");
    }

    if (!customer.address.city.trim()) {
      setCityError("City is required");
      isValid = false;
    } else {
      setCityError("");
    }

    if (!customer.address.postcode.trim()) {
      setPostCodeError("Post code or Zip code is required");
      isValid = false;
    } else if (!isValidPostalCode(customer.address.postcode)) {
      setPostCodeError("Please enter a valid postcode");
      isValid = false;
    } else {
      setPostCodeError("");
    }

    if (!customer.agree) {
      setAgreeError("You must agree before submitting");
      isValid = false;
    } else {
      setAgreeError("");
    }

    return isValid;
  };

  const nextStep = () => {
    if (step === 1 && !validateProfileImage()) return;
    if (step === 2 && !validateBasicDetails()) return;
    if (step === 3 && !validateAddressDetails()) return;

    setStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Convert base64 to blob
  const dataURLtoBlob = (dataURL) => {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  // Submit function to call Laravel API - UPDATED WITH POSTCODE
  const handleSubmit = async () => {
    const isProfileValid = validateProfileImage();
    const isStep2Valid = validateBasicDetails();
    const isStep3Valid = validateAddressDetails();

    if (!isProfileValid || !isStep2Valid || !isStep3Valid) {
      if (!isProfileValid) setStep(1);
      else if (!isStep2Valid) setStep(2);
      else if (!isStep3Valid) setStep(3);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      // Prepare form data for Laravel
      const formData = new FormData();

      // Add basic fields
      formData.append("agent_type", "mini");
      formData.append("first_name", customer.firstName);
      formData.append("last_name", customer.surname);
      formData.append("email", customer.email);
      formData.append("phone_number", customer.mobile);

      // Add address fields - INCLUDING POSTCODE
      formData.append("address_line1", customer.address.line1);
      formData.append("address_line2", customer.address.line2 || "");
      formData.append("address_line3", customer.address.line3 || "");
      formData.append("city", customer.address.city);
      formData.append("country_name", customer.address.country);
      formData.append("region", customer.address.county);
      formData.append("postcode", customer.address.postcode); // ← ADDED POSTCODE

      // Convert base64 image to blob and append
      if (customer.profileImage) {
        const blob = dataURLtoBlob(customer.profileImage);
        formData.append("photo", blob, "profile-image.jpg");
      }

      // Get auth token
      const token =
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("auth_token");

      const API_BASE_URL = API_URL;

      const response = await fetch(
        `${API_BASE_URL}/api/advertising-agents/apply`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || data.errors?.photo?.[0] || "Submission failed",
        );
      }

      // Success - call the onSubmit prop if provided
      if (onSubmit) {
        onSubmit(data);
      }

      // Reset form
      setCustomer((prev) => ({
        ...prev,
        profileImage: null,
        agree: false,
      }));
      setStep(1);
      setHasExistingApplication(true);
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError(
        error.message || "Failed to submit application. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setCustomer((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = (field, value) => {
    setCustomer((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  // Show message if already has application
  if (hasExistingApplication) {
    return (
      <div className="multi-form-container">
        <div className="existing-application">
          <h2>Application Already Submitted</h2>
          <p>You have already submitted an advertising agent application.</p>
          <p>Please wait for our team to review your application.</p>
          <button className="btn prev" onClick={() => navigate("/dashboard")}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="multi-form-container">
      <div className="request-header">
        <h2>Mini Advertising Agent Request Form</h2>
        <p>Confirm your details below to sent your request</p>
      </div>

      {fetchError && (
        <div className="error-message" style={{ marginBottom: "20px" }}>
          {fetchError}
        </div>
      )}

      {/* Progress Bar */}
      <div className="step-bar">
        {steps.map((item, i) => {
          const stepNum = i + 1;
          const isActive = step === stepNum;
          const isCompleted = step > stepNum;
          return (
            <div key={i} className="progress-step">
              <div
                className={`circle ${
                  isCompleted ? "completed" : isActive ? "active" : ""
                }`}
              >
                {isCompleted ? "✓" : stepNum}
              </div>
              <div className="step-text">
                <p className={`step-label ${isActive ? "active-label" : ""}`}>
                  {item.label}
                </p>
                <p className={`step-subtext ${isActive ? "active-sub" : ""}`}>
                  {item.sub}
                </p>
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`line ${
                    isCompleted ? "line-filled" : "line-empty"
                  }`}
                ></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="step-content">
        {step === 1 && (
          <div>
            <h3>Profile Image</h3>
            <hr />

            {/* IMAGE UPLOAD SECTION */}
            <div className="form-group">
              <label>
                Profile picture<span className="required">*</span>
              </label>

              <div
                className="image-upload-box"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleImageUpload(e.dataTransfer.files[0]);
                }}
              >
                {!customer.profileImage ? (
                  <>
                    <div className="picture-icon">
                      <BiImageAlt />
                    </div>

                    <p className="drag-text">
                      Drag and drop an image,or{" "}
                      <span
                        className="browse-text"
                        onClick={() => fileRef.current.click()}
                      >
                        Browse
                      </span>
                    </p>
                    <p className="info-text">Supports JPG, JPEG, PNG</p>

                    <input
                      type="file"
                      ref={fileRef}
                      hidden
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0])}
                    />
                  </>
                ) : (
                  <div className="preview-wrapper">
                    <button className="remove-btn" onClick={handleImageRemove}>
                      ✕
                    </button>
                    <img
                      src={customer.profileImage}
                      className="image-preview"
                      alt="Profile"
                    />
                  </div>
                )}
              </div>
              <div className="error-box">
                {profileError && <p className="error-text">{profileError}</p>}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3>Basic Details</h3>
            <hr />
            <div className="form-grid">
              <div className="form-group">
                <label>
                  First Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={customer.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  placeholder="Enter first name"
                />
                <div className="error-box">
                  {firstNameError && (
                    <p className="error-text">{firstNameError}</p>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>
                  Surname<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={customer.surname}
                  onChange={(e) => handleInputChange("surname", e.target.value)}
                  placeholder="Enter surname"
                />
                <div className="error-box">
                  {surnameError && <p className="error-text">{surnameError}</p>}
                </div>
              </div>
              <div className="form-group">
                <label>
                  Email<span className="required">*</span>
                </label>
                <input
                  type="email"
                  value={customer.email}
                  readOnly
                  disabled
                  className="readonly-input"
                  placeholder="Your email address"
                  style={{
                    backgroundColor: "#f5f5f5",
                    cursor: "not-allowed",
                    color: "#666",
                  }}
                />
                <div className="error-box">
                  {emailError && <p className="error-text">{emailError}</p>}
                </div>
                <small style={{ color: "#666", fontStyle: "italic" }}>
                  {/* Email cannot be changed for application verification */}
                </small>
              </div>
              <div className="form-group">
                <label>
                  Mobile<span className="required">*</span>
                </label>
                <PhoneInput
                  country={"gb"}
                  value={customer.mobile}
                  onChange={(value) => handleInputChange("mobile", value)}
                  className="select"
                />
                <div className="error-box">
                  {mobileError && <p className="error-text">{mobileError}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h3>Address</h3>
            <hr />
            <div className="form-grid">
              <div className="form-group">
                <label>
                  Address Line 1<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={customer.address.line1}
                  onChange={(e) => handleAddressChange("line1", e.target.value)}
                  placeholder="Enter line 1"
                />
                <div className="error-box">
                  {line1Error && <p className="error-text">{line1Error}</p>}
                </div>
              </div>
              <div className="form-group">
                <label>Address Line 2</label>
                <input
                  type="text"
                  value={customer.address.line2}
                  onChange={(e) => handleAddressChange("line2", e.target.value)}
                  placeholder="Enter line 2"
                />
                <div className="error-box" />
              </div>
              <div className="form-group">
                <label>Address Line 3</label>
                <input
                  type="text"
                  value={customer.address.line3}
                  onChange={(e) => handleAddressChange("line3", e.target.value)}
                  placeholder="Enter line 3"
                />
                <div className="error-box" />
              </div>
              <div className="form-group"></div>
              <div className="form-group">
                <label>
                  Country<span className="required">*</span>
                </label>
                <Select
                  options={Object.keys(regionOptions).map((c) => ({
                    value: c,
                    label: c,
                  }))}
                  value={
                    customer.address.country
                      ? {
                          value: customer.address.country,
                          label: customer.address.country,
                        }
                      : null
                  }
                  onChange={(option) => {
                    handleAddressChange("country", option.value);
                    handleAddressChange(
                      "county",
                      regionOptions[option.value][0],
                    );
                    handleAddressChange("city", "");
                  }}
                  className="select"
                  classNamePrefix="custom-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      border: "1.17px solid rgba(209, 209, 209, 1)",
                      borderRadius: "17.6px",
                      background: "rgba(253, 253, 253, 1)",
                      minHeight: "38px",
                      boxShadow: state.isFocused ? "none" : "none",
                      "&:hover": "none",
                      fontSize: "14px",
                      color: "rgba(105, 105, 105, 1)",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 8px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "black",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgba(105, 105, 105, 1)",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      marginTop: "2px",
                      zIndex: 9999,
                    }),
                    option: (base, { isSelected, isFocused }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "#e6f0ff"
                        : isFocused
                          ? "#e6f0ff"
                          : "#fff",
                      color: isSelected ? "#000" : "#000",
                      cursor: "pointer",
                    }),
                  }}
                />
                <div className="error-box" />
              </div>
              <div className="form-group">
                <label>
                  Region<span className="required">*</span>
                </label>
                <Select
                  options={
                    customer.address.country
                      ? regionOptions[customer.address.country].map((r) => ({
                          value: r,
                          label: r,
                        }))
                      : []
                  }
                  value={
                    customer.address.county
                      ? {
                          value: customer.address.county,
                          label: customer.address.county,
                        }
                      : null
                  }
                  onChange={(option) =>
                    handleAddressChange("county", option.value)
                  }
                  className="select"
                  classNamePrefix="custom-select"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      border: "1.17px solid rgba(209, 209, 209, 1)",
                      borderRadius: "17.6px",
                      background: "rgba(253, 253, 253, 1)",
                      minHeight: "38px",
                      boxShadow: state.isFocused ? "none" : "none",
                      "&:hover": "none",
                      fontSize: "14px",
                      color: "rgba(105, 105, 105, 1)",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "0 8px",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "black",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgba(105, 105, 105, 1)",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "#fff",
                      borderRadius: "6px",
                      marginTop: "2px",
                      zIndex: 9999,
                    }),
                    option: (base, { isSelected, isFocused }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "#e6f0ff"
                        : isFocused
                          ? "#e6f0ff"
                          : "#fff",
                      color: isSelected ? "#000" : "#000",
                      cursor: "pointer",
                    }),
                  }}
                />
                <div className="error-box" />
              </div>
              <div className="form-group">
                <label>
                  City<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={customer.address.city}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  placeholder="Enter city"
                />
                <div className="error-box">
                  {cityError && <p className="error-text">{cityError}</p>}
                </div>
              </div>

              <div className="form-group">
                <label>
                  PostCode/ Zip Code<span className="required">*</span>
                </label>
                <input
                  type="text"
                  value={customer.address.postcode}
                  onChange={(e) =>
                    handleAddressChange("postcode", e.target.value)
                  }
                  placeholder="Enter postcode"
                />
                <div className="error-box">
                  {postCodeError && (
                    <p className="error-text">{postCodeError}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Agent Agreement Section */}
            <div className="agent-agreement-section">
              <div className="agreement-divider-row">
                <span className="agreement-divider-line"></span>
              </div>
              <div className="agreement-divider-row">
                <span className="agreement-divider-line"></span>
              </div>

              <p className="agreement-text">
                Start by introducing TopCoin to your family, friends, and
                community.
              </p>

              <p className="agreement-text">
                As a TopCoin Agent, you may introduce customers and retailers to
                the platform. When they participate and receive TopCoin through
                normal platform activity, the same TopCoin recognition may also
                be recorded for you according to platform rules.
              </p>

              <div className="agreement-checkbox-row">
                <input
                  type="checkbox"
                  id="agree"
                  checked={customer.agree || false}
                  onChange={(e) => handleInputChange("agree", e.target.checked)}
                />
                <label htmlFor="agree">
                  By registering as a TopCoin UK Agent, you confirm that you
                  have read and agree to the{" "}
                  <strong>Agent Participation Agreement</strong>.
                </label>
              </div>

              <p className="agreement-link-text">
                To read the full terms, please visit the{" "}
                <a
                  href="/advertising-agent"
                  target="_blank"
                  className="agreement-link"
                >
                  Becoming Advertising Agent
                </a>{" "}
                page.
              </p>

              {agreeError && (
                <div className="agreement-error-box">
                  <p className="error-text">{agreeError}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Crop Modal */}
      {openCrop && (
        <div className="crop-modal">
          <div className="crop-container">
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={onCropComplete}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "300px",
                  position: "relative",
                },
                cropAreaStyle: {
                  border: "2px solid #0d6efd",
                },
                mediaStyle: {
                  transform: `rotate(${rotation}deg)`,
                },
              }}
            />

            {/* Controls */}
            <div className="controls">
              <div className="control-group">
                <label>Zoom: {zoom.toFixed(1)}</label>
                <div className="zoom-buttons">
                  <button
                    type="button"
                    onClick={() => setZoom((prev) => Math.min(prev + 0.1, 3))}
                  >
                    <BiZoomIn />
                  </button>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={() => setZoom((prev) => Math.max(prev - 0.1, 1))}
                  >
                    <BiZoomOut />
                  </button>
                </div>
              </div>

              <div className="control-group">
                <label>Rotate: {rotation}°</label>
                <div className="rotate-buttons">
                  <button
                    type="button"
                    onClick={() => setRotation((prev) => (prev + 90) % 360)}
                  >
                    <FiRotateCw />
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={rotation}
                    onChange={(e) => setRotation(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="crop-buttons">
              <button type="button" onClick={() => setOpenCrop(false)}>
                Cancel
              </button>
              <button type="button" onClick={handleCropSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="btn-row-container">
        {step > 1 && (
          <button className="btn prev" onClick={prevStep}>
            Previous
          </button>
        )}
        {step < 3 && (
          <button className="btn next" onClick={nextStep}>
            Next
          </button>
        )}
        {step === 3 && (
          <button
            className="btn submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        )}
      </div>

      {submitError && (
        <div
          className="error-message"
          style={{ color: "red", marginTop: "10px", textAlign: "center" }}
        >
          {submitError}
        </div>
      )}
    </div>
  );
}
